import {
  ChangeDetectorRef,
  Component,
  inject,
  Inject,
  OnInit,
} from '@angular/core';
import { MaterialModule } from '../../../shared/material/material.module';
import {
  Faq,
  FaqControllerService,
} from '../../../core/generated';
import { GroupFaq } from '../../../shared/enums';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { FormBuilder, Validators } from '@angular/forms';
import { ContainerComponent } from '../../../shared/container/container.component';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

@Component({
  selector: 'app-admin-faq-dialog',
  standalone: true,
  imports: [MaterialModule],
  template: `
    <div class="modal-backdrop">
      <div class="modal-container">
        <div class="modal">
          <div class="modal-header">
            <h3 class="modal-title">{{ data.faq ? 'Editar FAQ' : 'Nova FAQ' }}</h3>
          </div>

          <mat-dialog-content class="modal-body">
            <form [formGroup]="faqForm">
              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>Grupo</mat-label>
                <mat-select formControlName="group">
                  @for (option of groupFaqOptions; track option.key) {
                  <mat-option [value]="option.key">{{ option.value }}</mat-option>
                  }
                </mat-select>
                <mat-error *ngIf="faqForm.get('group')?.hasError('required')">Campo obrigatório</mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>Pergunta</mat-label>
                <input matInput formControlName="question" />
                <mat-error *ngIf="faqForm.get('question')?.hasError('required')">Campo obrigatório</mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>Resposta</mat-label>
                <textarea matInput formControlName="response" rows="4"></textarea>
                <mat-error *ngIf="faqForm.get('response')?.hasError('required')">Campo obrigatório</mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>Link</mat-label>
                <input matInput formControlName="link" placeholder="ex: /shopping" />
              </mat-form-field>

              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>Label do Link</mat-label>
                <input matInput formControlName="labellink" />
              </mat-form-field>
            </form>
          </mat-dialog-content>

          <mat-dialog-actions class="modal-footer">
            <button mat-button class="btn btn-ghost" (click)="onCancel()">
              <mat-icon>close</mat-icon> Cancelar
            </button>
            <button mat-flat-button color="primary" class="btn btn-primary" (click)="onSave()" [disabled]="faqForm.invalid">
              <mat-icon>save</mat-icon> Salvar
            </button>
          </mat-dialog-actions>
        </div>
      </div>
    </div>
  `,
  styles: [`
    mat-form-field { 
      width: 100%; 
      margin-bottom: var(--spacing-sm); 
    }
  `]
})
export class AdminFaqDialogComponent {
  fb = inject(FormBuilder);
  dialogRef = inject(MatDialogRef<AdminFaqDialogComponent>);
  
  groupFaqOptions = Object.keys(GroupFaq).map((key) => ({
    key,
    value: GroupFaq[key as keyof typeof GroupFaq]
  }));

  faqForm = this.fb.group({
    id: [null as number | null],
    group: ['', Validators.required],
    question: ['', Validators.required],
    response: ['', Validators.required],
    link: [''],
    labellink: [''],
  });

  constructor(@Inject(MAT_DIALOG_DATA) public data: { faq?: Faq }) {
    if (this.data.faq) {
      this.faqForm.patchValue({
        id: this.data.faq.id || null,
        group: this.data.faq.group || '',
        question: this.data.faq.question || '',
        response: this.data.faq.response || '',
        link: this.data.faq.link || '',
        labellink: this.data.faq.labellink || '',
      });
    }
  }

  onCancel() {
    this.dialogRef.close();
  }

  onSave() {
    if (this.faqForm.valid) {
      const faqData: Faq = {
        id: this.faqForm.controls.id.value || undefined,
        group: this.faqForm.controls.group.value || '',
        question: this.faqForm.controls.question.value || '',
        response: this.faqForm.controls.response.value || '',
        link: this.faqForm.controls.link.value || '',
        labellink: this.faqForm.controls.labellink.value || '',
      };
      this.dialogRef.close(faqData);
    }
  }
}

@Component({
  selector: 'app-admin-faq',
  standalone: true,
  imports: [MaterialModule, ContainerComponent],
  templateUrl: './admin-faq.component.html',
  styleUrl: './admin-faq.component.scss'
})
export class AdminFaqComponent implements OnInit {
  faqService = inject(FaqControllerService);
  _snackBar = inject(MatSnackBar);
  cd = inject(ChangeDetectorRef);
  dialog = inject(MatDialog);

  isLoading = false;
  faqs: Faq[] = [];

  async ngOnInit() {
    await this.loadFaqs();
  }

  async loadFaqs() {
    this.isLoading = true;
    try {
      this.faqs = await firstValueFrom(this.faqService.findAll8());
    } finally {
      this.isLoading = false;
    }
  }

  openFaqDialog(faq?: Faq) {
    const dialogRef = this.dialog.open(AdminFaqDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: { faq }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.saveFaq(result);
      }
    });
  }

  async saveFaq(body: Faq) {
    try {
      if (!body.id) {
        const faq = await firstValueFrom(this.faqService.save12(body));
        this.faqs.push(faq);
        this._snackBar.open('FAQ criado com sucesso!', 'Fechar', {
          duration: 5000,
        });
      } else {
        const faqId = body.id;
        const faq = await firstValueFrom(this.faqService.update12(faqId, body));
        this.faqs = this.faqs.filter((item) => item.id !== faqId);
        this.faqs.push(faq);
        this._snackBar.open('FAQ atualizado com sucesso!', 'Fechar', {
          duration: 5000,
        });
      }
      this.cd.detectChanges();
    } catch (error) {
      this._snackBar.open('Erro ao salvar FAQ', 'Fechar', {
        duration: 5000,
      });
    }
  }

  editFaq(faq: Faq) {
    this.openFaqDialog(faq);
  }

  async deleteFaq(id: any) {
    if (!confirm('Deseja realmente excluir esta FAQ?')) {
      return;
    }

    try {
      const resp = await firstValueFrom(this.faqService.deleteById12(id));
      if (resp === null) {
        this.faqs = this.faqs.filter((item) => item.id !== id);
        this._snackBar.open('FAQ excluído com sucesso!', 'Fechar', {
          duration: 5000,
        });
        this.cd.detectChanges();
      }
    } catch (error) {
      this._snackBar.open('Erro ao excluir FAQ', 'Fechar', {
        duration: 5000,
      });
    }
  }
}
